import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck, HeartHandshake, Home, CheckCircle2, HelpingHand, ArrowRight } from 'lucide-react';
import Seo from './Seo';
import Breadcrumbs from './Breadcrumbs';
import FaqAccordion from './FaqAccordion';
import RelatedLinks from './RelatedLinks';
import { BRAND } from '../../seo/siteConfig';
import './seo.css';

/**
 * SeoPage — presentational template. Receives a fully-composed page object
 * from the registry (registry.getPage) and renders a complete, compliant page.
 */
const SeoPage = ({ page }) => {
  if (!page) return null;
  const {
    canonicalPath, title, description, image, eyebrow, h1, heroSubtitle,
    breadcrumbs, intro, complianceNote, whoFor, sections, howWeHelp, heroImage,
    funding, location, related, faqs, cta, phone, schema, applyLink,
  } = page;
  const applyTo = applyLink || '/apply';

  return (
    <div className="seo-page">
      <Seo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        image={image}
        breadcrumbs={breadcrumbs}
        faqs={faqs}
        schema={schema}
      />

      {/* HERO */}
      <header className={`sp-hero ${heroImage ? 'has-image' : ''}`}>
        {heroImage && (
          <img
            className="sp-hero-img"
            src={heroImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
          />
        )}
        <div className="sp-hero-inner">
          <Breadcrumbs items={breadcrumbs} />
          {eyebrow && <p className="sp-eyebrow">{eyebrow}</p>}
          <h1 className="sp-h1">{h1}</h1>
          {heroSubtitle && <p className="sp-hero-sub">{heroSubtitle}</p>}
          <div className="sp-hero-cta">
            <Link to={applyTo} className="sp-btn sp-btn-primary">{cta.label} <ArrowRight size={18} /></Link>
            <a href={`tel:${phone}`} className="sp-btn sp-btn-ghost"><Phone size={17} /> Speak With PGSRH</a>
          </div>
          <div className="sp-trust-chips">
            <span><ShieldCheck size={15} /> {BRAND.ddapLine}</span>
            <span><Home size={15} /> Structured recovery housing</span>
            <span><MapPin size={15} /> {BRAND.region}</span>
          </div>
        </div>
      </header>

      <main className="sp-body">
        {/* INTRO + compliance */}
        <section className="sp-section sp-intro">
          <div className="sp-intro-text">
            {intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <aside className="sp-note-card">
            <div className="sp-note-icon"><ShieldCheck size={20} /></div>
            <h2 className="sp-note-title">Supportive housing &mdash; not clinical treatment</h2>
            <p>{complianceNote}</p>
          </aside>
        </section>

        {/* WHO IT'S FOR */}
        {whoFor && (
          <section className="sp-section">
            <h2 className="sp-h2">{whoFor.heading}</h2>
            <span className="sp-underline" />
            <ul className="sp-check-list">
              {whoFor.items.map((it, i) => (
                <li key={i}><CheckCircle2 size={18} /> <span>{it}</span></li>
              ))}
            </ul>
          </section>
        )}

        {/* UNIQUE BODY SECTIONS */}
        {sections.map((s, i) => (
          <section className="sp-section" key={i}>
            <h2 className="sp-h2">{s.h}</h2>
            <span className="sp-underline" />
            {s.p && s.p.map((para, j) => <p key={j} className="sp-p">{para}</p>)}
            {s.list && (
              <ul className="sp-check-list">
                {s.list.map((it, j) => <li key={j}><CheckCircle2 size={18} /> <span>{it}</span></li>)}
              </ul>
            )}
          </section>
        ))}

        {/* HOW PGSRH MAY HELP */}
        <section className="sp-section sp-help">
          <div className="sp-help-icon"><HeartHandshake size={22} /></div>
          <h2 className="sp-h2">How PGSRH may help</h2>
          <span className="sp-underline" />
          {howWeHelp.map((p, i) => <p key={i} className="sp-p">{p}</p>)}
        </section>

        {/* FUNDING (optional, cautious wording) */}
        {funding && (
          <section className="sp-section sp-funding">
            <div className="sp-help-icon"><HelpingHand size={22} /></div>
            <h2 className="sp-h2">{funding.heading}</h2>
            <span className="sp-underline" />
            {funding.paragraphs.map((p, i) => <p key={i} className="sp-p">{p}</p>)}
            <div className="sp-funding-note">
              <p>{funding.note}</p>
            </div>
            <div className="sp-inline-cta">
              <Link to="/apply#apply-form" className="sp-btn sp-btn-primary sp-btn-sm">Ask About Funding Options <ArrowRight size={16} /></Link>
              <Link to="/recovery-housing-funding-assistance/" className="sp-textlink">View all funding &amp; scholarship options</Link>
            </div>
          </section>
        )}

        {/* LOCATION DETAILS (optional, verified only) */}
        {location && (
          <section className="sp-section sp-location">
            <h2 className="sp-h2">Visit or contact this recovery house</h2>
            <span className="sp-underline" />
            <div className="sp-location-card">
              <div className="sp-location-item">
                <MapPin size={18} />
                <div>
                  <strong>{location.label}</strong>
                  <p>{location.hasStreet ? `${location.street}, ${location.city}, ${location.state} ${location.zip}` : `${location.city}, ${location.state} ${location.zip}`}</p>
                </div>
              </div>
              <div className="sp-location-item">
                <Phone size={18} />
                <div><strong>Call</strong><p><a href={`tel:${location.phone}`}>{location.phone}</a></p></div>
              </div>
              <div className="sp-location-item">
                <Mail size={18} />
                <div><strong>Email</strong><p><a href={`mailto:${BRAND.email}`}>{BRAND.email}</a></p></div>
              </div>
            </div>
          </section>
        )}

        {/* RELATED INTERNAL LINKS */}
        {related && related.length > 0 && (
          <section className="sp-section">
            <h2 className="sp-h2">Related recovery housing pages</h2>
            <span className="sp-underline" />
            <RelatedLinks links={related} />
          </section>
        )}

        {/* FAQ */}
        {faqs && faqs.length > 0 && (
          <section className="sp-section">
            <h2 className="sp-h2">Frequently asked questions</h2>
            <span className="sp-underline" />
            <FaqAccordion faqs={faqs} />
          </section>
        )}
      </main>

      {/* CTA BLOCK */}
      <section className="sp-cta-block">
        <div className="sp-cta-inner">
          <h2>{cta.headline || 'Ready to take the next step?'}</h2>
          <p>{cta.sub}</p>
          <div className="sp-cta-actions">
            <Link to={applyTo} className="sp-btn sp-btn-white">{cta.label} <ArrowRight size={18} /></Link>
            <a href={`tel:${phone}`} className="sp-btn sp-btn-outline-white"><Phone size={17} /> {phone}</a>
            <Link to="/contact" className="sp-btn sp-btn-outline-white">Contact PGSRH</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeoPage;
